// Newsletter sign-up: the site's Mailchimp connection and its opt-ins
// (/api/admin/sites/:id/newsletter). The API key is write-only.

import { apiFetch, isApiError } from './api';
import { serverOrigin } from './server-origin';
import { useAgentStore } from '../stores/agent';

export interface MailchimpConnection {
  siteId: string;
  enabled: boolean;
  audienceId: string;
  audienceName: string | null;
  doubleOptIn: boolean;
  tags: string[];
  dataCenter: string | null;
  updatedAt: string;
}

export interface Audience {
  id: string;
  name: string;
  members: number;
}

export interface OptIn {
  visitorId: string;
  name: string | null;
  email: string | null;
  optInAt: string;
  status: 'pending' | 'synced' | 'failed' | null;
  error: string | null;
}

export async function getNewsletter(siteId: string): Promise<{
  mailchimp: MailchimpConnection | null;
  counts: { total: number; synced: number; pending: number; failed: number };
  encryptionAvailable: boolean;
}> {
  return apiFetch(`/api/admin/sites/${siteId}/newsletter`);
}

export async function listAudiences(siteId: string, apiKey?: string): Promise<Audience[]> {
  const { audiences } = await apiFetch<{ audiences: Audience[] }>(`/api/admin/sites/${siteId}/newsletter/audiences`, {
    method: 'POST',
    body: JSON.stringify(apiKey ? { apiKey } : {}),
  });
  return audiences;
}

export async function connectMailchimp(
  siteId: string,
  input: { apiKey?: string; audienceId: string; doubleOptIn: boolean; tags: string[]; enabled: boolean },
): Promise<MailchimpConnection> {
  const { mailchimp } = await apiFetch<{ mailchimp: MailchimpConnection }>(`/api/admin/sites/${siteId}/newsletter`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
  return mailchimp;
}

export async function disconnectMailchimp(siteId: string): Promise<void> {
  await apiFetch(`/api/admin/sites/${siteId}/newsletter`, { method: 'DELETE' });
}

export async function syncNewsletter(siteId: string): Promise<number> {
  const { queued } = await apiFetch<{ queued: number }>(`/api/admin/sites/${siteId}/newsletter/sync`, { method: 'POST' });
  return queued;
}

export async function listOptIns(siteId: string): Promise<OptIn[]> {
  const { subscribers } = await apiFetch<{ subscribers: OptIn[] }>(`/api/admin/sites/${siteId}/newsletter/subscribers`);
  return subscribers;
}

/** Download every opt-in as CSV (an authenticated fetch, saved as a file). */
export async function downloadOptInsCsv(siteId: string): Promise<void> {
  const res = await fetch(`${serverOrigin()}/api/admin/sites/${siteId}/newsletter/subscribers?format=csv`, {
    headers: { Authorization: `Bearer ${useAgentStore.getState().tokens?.accessToken ?? ''}` },
  });
  if (!res.ok) throw new Error(`export failed (${res.status})`);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = 'newsletter-subscribers.csv';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function newsletterError(err: unknown): string {
  if (!isApiError(err)) return err instanceof Error ? err.message : 'The request failed.';
  const body = (err.body ?? {}) as { message?: string; error?: string };
  return body.message ?? `The request failed (${err.status}).`;
}
